"use client"

import type { 
  WorkflowItem, 
  WorkflowStage, 
  WorkflowApiResponse, 
  FieldConfiguration, 
  WorkflowConfiguration,
  AssignedUser,
  WorkflowSearchRequest,
  SuggestionsApiResponse,
  UserSearchResponse,
  UserSearchApiResponse,
  UserReportDto,
  ClientCustomFieldsResponse
} from "@/types/workflow"
import type { UserViewsResponse, SaveViewSettingsRequestDto } from "@/types/saved-view"
import { useApi } from "@/lib/api"
import { useEntityType } from "@/contexts/entity-type-context"

// Field mapping for the API select statement
const API_SELECT_FIELDS = [
  'id',
  'WorkflowHubId',
  'Actions',
  'Stage',
  'HealthStatus',
  'WorkerFullName',
  'JobTitle',
  'WorkerPhone',
  'WorkerEmail',
  'ProfileType',
  'EligibilityToWork',
  'PermitExpirationDate',
  'SupplierName',
  'ClientName',
  'InternalCompany',
  'Branch',
  'StartDate',
  'EndDate',
  'WorkerLocation',
  'PrimaryJobOwnerFullName',
  'RecruiterFullName',
  'AtsPlacementId',
  'VmsId',
  'PrimaryPayRate',
  'PrimaryBillRate',
  'OtEligible',
  'OtPayRate',
  'OtBillRate',
  'TimesheetMethod',
  'ManagerFullName',
  'ManagerEmail',
  'Duration',
  'WorkerProfileStatus',
  'AssignedToUser'
].join(',')

// Utility functions
const transformApiResponseToWorkflowItem = (apiItem: any): WorkflowItem => {
  // Use the actual ID from the API response
  const id = apiItem.id || apiItem.atsPlacementId || apiItem.vmsId || `item-${Date.now()}-${Math.random()}`
  
  // Compute full name - use workerFullName from API or fallback
  const workerFullName = apiItem.workerFullName || 'Unknown Worker'

  // Map work order status to stage
  const stage = mapWorkOrderStatusToStage(apiItem.workOrderStatus)

  return {
    // Core identification
    id,
    workflowHubId: apiItem.workflowHubId,
    atsPlacementId: apiItem.atsPlacementId,
    vmsId: apiItem.vmsId,

    // Worker information
    workerEmail: apiItem.workerEmail,
    workerPhone: apiItem.workerPhone,
    workerLocation: apiItem.workerLocation,
    workerProfileStatus: apiItem.workerProfileStatus,
    workerFullName,

    // Assignment and management
    assignedToUserEmail: apiItem.assignedToUserEmail,
    assignedToUserFullName: apiItem.assignedToUserFullName,
    managerFullName: apiItem.managerFullName,
    managerEmail: apiItem.managerEmail,
    recruiterFullName: apiItem.recruiterFullName,
    primaryJobOwnerFullName: apiItem.primaryJobOwnerFullName,

    // Company and client information
    clientName: apiItem.clientName,
    supplierName: apiItem.supplierName,
    internalCompany: apiItem.internalCompany,
    branch: apiItem.branch,

    // Job details
    jobTitle: apiItem.jobTitle,
    profileType: apiItem.profileType,
    workOrderStatus: apiItem.workOrderStatus,

    // Legal and compliance
    eligibilityToWork: apiItem.eligibilityToWork,
    permitExpirationDate: apiItem.permitExpirationDate,

    // Financial information
    primaryPayRate: apiItem.primaryPayRate,
    primaryBillRate: apiItem.primaryBillRate,
    otEligible: apiItem.otEligible,
    otPayRate: apiItem.otPayRate,
    otBillRate: apiItem.otBillRate,

    // Dates
    startDate: apiItem.startDate,
    endDate: apiItem.endDate,
    onboardingPackageSentDate: apiItem.onboardingPackageSentDate,
    onboardingPackageCompleteDate: apiItem.onboardingPackageCompleteDate,

    // Additional fields
    duration: apiItem.duration,
    timesheetMethod: apiItem.timesheetMethod,
    healthStatus: apiItem.healthStatus,

    // System fields
    isDeleted: apiItem.isDeleted,
    createdByProfileId: apiItem.createdByProfileId,
    createdDateTime: apiItem.createdDateTime,
    lastModifiedByProfileId: apiItem.lastModifiedByProfileId,
    lastModifiedDateTime: apiItem.lastModifiedDateTime,

    // Computed fields for UI compatibility
    stage,
    client: apiItem.clientName, // Alias
    recruiter: apiItem.recruiterFullName, // Alias
    assignedTo: apiItem.assignedToUserFullName, // Alias

    // Preserve all other fields from the API response (including custom fields like textField2)
    ...apiItem
  }
}

const mapWorkOrderStatusToStage = (workOrderStatus?: string): WorkflowStage => {
  if (!workOrderStatus) return 'onboarding'
  
  const status = workOrderStatus.toLowerCase()
  
  if (status.includes('active') || status.includes('assignment')) return 'active'
  if (status.includes('extending') || status.includes('extension')) return 'extension'
  if (status.includes('offboarding') || status.includes('terminating')) return 'offboarding'
  if (status.includes('complete') || status.includes('closed')) return 'complete'
  
  return 'onboarding' // Default
}

const buildFilterFromStageAndFilters = (
  stage: WorkflowStage | undefined,
  customFilter: string[] | undefined
): string | null => {
  const filterConditions: string[] = []

  // Include stage only if provided and not already present in custom filters
  const customHasStage = Array.isArray(customFilter)
    ? customFilter.some(f => /\bStage\s+eq\s+'/i.test(f))
    : false
  if (stage && !customHasStage) {
    filterConditions.push(`Stage eq '${stage}'`)
  }

  // Add any additional custom filters (already formatted OData expressions)
  if (customFilter && customFilter.length > 0) {
    filterConditions.push(...customFilter)
  }

  return filterConditions.length > 0 ? filterConditions.join(' and ') : null
}

/**
 * Sanitizes and validates search query for OData filters
 * @param query - Raw search query from user input
 * @returns Sanitized query safe for OData interpolation, or null if invalid
 */
const sanitizeSearchQuery = (query: string): string | null => {
  // Trim whitespace
  const trimmed = query.trim();
  
  // Reject if too short or too long
  if (trimmed.length < 2 || trimmed.length > 50) {
    return null;
  }
  
  // Remove or normalize control characters (keep only printable characters)
  const normalized = trimmed.replace(/[\x00-\x1F\x7F-\x9F]/g, '');
  
  // Escape single quotes for OData (replace ' with '')
  const escaped = normalized.replace(/'/g, "''");
  
  // Convert to lowercase for case-insensitive search
  return escaped.toLowerCase();
}

/**
 * Custom hook for Workflow API operations
 * Simple wrapper around WorkflowApiService
 */
export function useWorkflowApi() {
  const { workflowApi, fboReportApi, mainApi } = useApi()
  const { entityType } = useEntityType()

  // Search workflow items with parameters
  const searchWorkflowItems = async (
    stage: WorkflowStage | undefined,
    customFilter: string[] | undefined,
    fieldConfigurations: FieldConfiguration[],
    page: number = 1,
    pageSize: number = 5,
    search: string = '*',
    select?: string,
    filter?: string,
    orderBy?: string,
    facetKeys?: string[]
  ): Promise<WorkflowApiResponse> => {
    try {
      const searchRequest: WorkflowSearchRequest = {
        select: select || API_SELECT_FIELDS,
        filter: filter || buildFilterFromStageAndFilters(stage, customFilter),
        orderBy: orderBy ?? 'CreatedDateTime desc',
        top: pageSize,
        skip: (page - 1) * pageSize,
        search,
        facets: facetKeys ?? null,
        count: true
      }

      const responseData = await workflowApi.post<any>('/api/Workflow/search', searchRequest)
      
      // Handle different possible response structures
      let apiData: any[]
      let totalCount: number = 0
      
      if (Array.isArray(responseData)) {
        apiData = responseData
        totalCount = responseData.length
      } else if (responseData && Array.isArray(responseData.data)) {
        apiData = responseData.data
        totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.data.length
      } else if (responseData && Array.isArray(responseData.value)) {
        apiData = responseData.value
        totalCount = responseData['@odata.count'] || responseData.totalCount || responseData.value.length
      } else {
        throw new Error('API response is not in expected format')
      }
      
      // Transform API response to our internal format
      const transformedData = apiData.map(transformApiResponseToWorkflowItem)

      // Extract facets if present
      let facets: Record<string, { value: string; count: number }[]> | undefined = undefined;
      if (responseData['@search.facets'] && typeof responseData['@search.facets'] === 'object') {
        facets = responseData['@search.facets'];
      }

      // Calculate pagination info based on actual total count
      const hasNextPage = (page * pageSize) < totalCount
      const hasPreviousPage = page > 1

      return {
        data: transformedData,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        facets
      }
    } catch (error) {
      throw error
    }
  }

  // Update a workflow item field
  const updateWorkflowItemField = async (
    itemId: string, 
    field: string, 
    value: any
  ): Promise<{ successUpdateCount: number; failedUpdateCount: number; messages: string[] }> => {
    try {
      const result = await workflowApi.put<{ successUpdateCount: number; failedUpdateCount: number; messages: string[] }>(`/api/Workflow/report/${itemId}`, { Updates: { [field]: value } })
      return result
    } catch (error) {
      throw error
    }
  }

  // Bulk update workflow items
  const bulkUpdateWorkflowItems = async (
    itemIds: string[], 
    field: string, 
    value: any
  ): Promise<WorkflowItem[]> => {
    try {
      // This would be implemented based on your bulk update API endpoint
      throw new Error('Bulk update API endpoint not yet implemented')
    } catch (error) {
      throw error
    }
  }

  // Bulk update reports (new endpoint)
  const bulkUpdateReports = async (
    updates: { id: string; updates: Record<string, any> }[]
  ): Promise<any> => {
    try {
      return await workflowApi.put<any>(
        '/api/Workflow/reports/bulk',
        { updates }
      );
    } catch (error) {
      throw error;
    }
  };

  // Process workflow action (stage changes, etc.)
  const processWorkflowAction = async (
    itemId: string, 
    action: string
  ): Promise<WorkflowItem> => {
    try {
      // This would be implemented based on your action processing API endpoint
      throw new Error('Action processing API endpoint not yet implemented')
    } catch (error) {
      throw error
    }
  }

  // Fetch workflow configuration
  const fetchWorkflowConfiguration = async (viewId?: string | null): Promise<WorkflowConfiguration> => {
    let url = '/api/Workflow/configurations'
    const params: Record<string, string> = { entityType }
    if (viewId) {
      params.viewId = viewId
    }
    const query = new URLSearchParams(params).toString()
    url += `?${query}`

    const config = await workflowApi.get<WorkflowConfiguration>(url)
    if (config && Array.isArray(config.stageConfigurations)) {
      config.stageConfigurations.forEach((stageConfig: any) => {
        if (Array.isArray(stageConfig.fieldConfigurations)) {
          stageConfig.fieldConfigurations.sort((a: any, b: any) => a.order - b.order);
        }
      });
    }
    return config;
  }

  // Update a field configuration
  const updateFieldConfiguration = async (
    id: string,
    fieldConfiguration: any,
    stageIds: string[],
    currentViewSettingsId?: string | null
  ): Promise<any> => {
    const requestPayload = { 
      fieldConfiguration, 
      stageIds, 
      currentViewSettingsId 
    };
 
    return await workflowApi.put<any>(
      `/api/Workflow/field/${id}`,
      requestPayload
    )
  }

  // Create custom field configuration
  const createCustomFieldConfiguration = async (payload: any): Promise<any> => {
    const requestPayload = { ...payload, entityType };
    
    return await workflowApi.post<any>('/api/Workflow/field/custom', requestPayload)
  }

  // Fetch assigned users
  const fetchAssignedUsers = async (): Promise<AssignedUser[]> => {
    try {
      const assignedUsers = await workflowApi.get<AssignedUser[]>('/api/Workflow/assigned-users')
      return Array.isArray(assignedUsers) ? assignedUsers : [];
    } catch (error) {
      throw error;
    }
  }

  // Update stage field order
  const updateStageFieldOrder = async (stageId: string, fieldOrder: string[]): Promise<void> => {
    await workflowApi.put<void>(`/api/Workflow/stage/${stageId}/fields`, fieldOrder)
  }

  // Delete field configuration
  const deleteFieldConfiguration = async (fieldConfigId: string): Promise<void> => {
    try {
      await workflowApi.delete<void>(`/api/Workflow/field/${fieldConfigId}/reset`)
    } catch (error) {
      let errorMsg = `Failed to delete field configuration`;
      if (error instanceof Error) {
        errorMsg = error.message;
      }
      throw new Error(errorMsg);
    }
  }

  // Toggle field visibility on stage
  const toggleFieldVisibility = async (stageId: string, fieldConfigurations: string[]): Promise<void> => {
    await workflowApi.put<void>(`/api/Workflow/stage/${stageId}/fields`, fieldConfigurations)
  }

  // Fetch search suggestions using the Azure suggester
  const fetchSearchSuggestions = async (
    searchText: string,
    field: string,
    top: number = 5
  ): Promise<SuggestionsApiResponse> => {
    try {
      const response = await workflowApi.post<SuggestionsApiResponse>(
        '/api/Workflow/suggestions',
        { 
          ColumnCode: field, 
          SearchValue: searchText, 
          Top: top 
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Fetch views
  const fetchViews = async (): Promise<UserViewsResponse> => {
    try {
      const url = `/api/Workflow/views?${new URLSearchParams({ entityType }).toString()}`
      const response = await workflowApi.get<UserViewsResponse>(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Fetch specific view
  const fetchView = async (viewId: string): Promise<any> => {
    try {
      const url = `/api/Workflow/views/${viewId}?${new URLSearchParams({ entityType }).toString()}`
      const response = await workflowApi.get<any>(url);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Save view settings
  const saveViewSettings = async (request: SaveViewSettingsRequestDto): Promise<any> => {
    try {
      const requestPayload = { ...request, entityType }
      const response = await workflowApi.post<any>('/api/Workflow/view-settings', requestPayload);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Save view state
  const saveViewState = async (request: SaveViewSettingsRequestDto): Promise<any> => {
    try {
      const response = await workflowApi.post<any>('/api/Workflow/view-state', request);
      return response;
    } catch (error) {
      throw error;
    }
  };

  // Delete view settings
  const deleteViewSettings = async (id: string): Promise<void> => {
    try {
      await workflowApi.delete<void>(`/api/Workflow/view-settings/${id}`);
    } catch (error) {
      throw error;
    }
  };

  // Update view visibility
  const updateViewVisibility = async (id: string, remove: boolean): Promise<void> => {
    try {
      await workflowApi.put<void>(`/api/Workflow/view/${id}?remove=${remove}`, {});
    } catch (error) {
      throw error;
    }
  };

  // Search users for private column authorization
  const searchUsers = async (searchQuery: string): Promise<UserReportDto[]> => {
    try {
      // Input validation and sanitization
      const sanitizedQuery = sanitizeSearchQuery(searchQuery);
      if (!sanitizedQuery) {
        return [];
      }

      const filterConditions = [
        `((contains(tolower(FirstName),'${sanitizedQuery}')) or (contains(tolower(LastName),'${sanitizedQuery}')) or (contains(tolower(PrimaryEmail),'${sanitizedQuery}')))`,
        `((ProfileTypeId eq 2) or (ProfileTypeId eq 99))`
      ].join(' and ');

      const params = new URLSearchParams({
        '$orderby': 'ContactId',
        '$top': '100',
        '$select': 'EntityId,FirstName,LastName,PrimaryEmail',
        '$filter': filterConditions,
        '$count': 'true'
      });

      const response = await fboReportApi.get<UserSearchResponse>(`/report/33?${params.toString()}`);
      
      // Transform the API response to UserReportDto format
      return response.value.map((user: UserSearchApiResponse): UserReportDto => ({
        profileId: user.EntityId,
        fullName: `${user.FirstName} ${user.LastName}`.trim() || user.PrimaryEmail,
        email: user.PrimaryEmail
      }));

    } catch (error) {
      console.error('Failed to search users:', error);
      throw error;
    }
  };

  const fetchClientCustomFields = async (
    organizationId: number,
    entityType: number = 17
  ): Promise<ClientCustomFieldsResponse | null> => {
    try {
      const url = `/api/ClientBasedEntityCustomField/GetCustomFields?organizationId=${organizationId}&entityType=${entityType}`;
      const response = await mainApi.get<ClientCustomFieldsResponse>(url);
      return response;
    } catch (error) {
      console.error('Failed to fetch client custom fields:', error);
      return null;
    }
  };

  return {
    searchWorkflowItems,
    updateWorkflowItemField,
    bulkUpdateWorkflowItems,
    processWorkflowAction,
    fetchWorkflowConfiguration,
    updateFieldConfiguration,
    createCustomFieldConfiguration,
    fetchAssignedUsers,
    updateStageFieldOrder,
    deleteFieldConfiguration,
    toggleFieldVisibility,
    bulkUpdateReports,
    fetchSearchSuggestions,
    fetchViews,
    fetchView,
    saveViewSettings,
    saveViewState,
    deleteViewSettings,
    updateViewVisibility,
    searchUsers,
    fetchClientCustomFields,
  }
} 